var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
};
import * as React from 'react';
import { SchedulerItem } from './SchedulerItem';
import { intersects, first } from '../utils';
import { useSchedulerViewSlotsContext } from '../context';
import { getRect, setRect } from '../views/common/utils';
import { BORDER_WIDTH } from '../constants';
import { useInternationalization } from '@progress/kendo-react-intl';
import { SchedulerItemContent } from './SchedulerItemContent';
// tslint:disable:max-line-length
/**
 * Represents the default `viewItem` component rendered by the [KendoReact Scheduler component]({% slug overview_scheduler %}).
 *
 * This is a composite component of the [`SchedulerItem`]({% slug api_scheduler_scheduleritem %}), extending it to provide proper positioning inside a view.
 */
export var SchedulerViewItem = React.forwardRef(function (props, ref) {
    // tslint:enable:max-line-length
    var itemProp = props.item, _ref = props._ref, itemProps = __rest(props, ["item", "_ref"]);
    var item = React.useRef(null);
    var viewItem = React.useRef(null);
    React.useImperativeHandle(viewItem, function () { return ({ props: props, element: item.current && item.current.element }); });
    React.useImperativeHandle(_ref, function () { return item.current; });
    React.useImperativeHandle(ref, function () { return viewItem.current; });
    var Item = itemProp || defaultProps.item;
    var intl = useInternationalization();
    var viewSlots = useSchedulerViewSlotsContext()[0];
    var _a = React.useState(true), display = _a[0], setDisplay = _a[1];
    var _b = React.useState(false), visible = _b[0], setVisible = _b[1];
    var itemTime = React.useMemo(function () { return props.isAllDay
        ? intl.toString(props.zonedStart, 't')
        : intl.format('{0:t} - {1:t}', props.zonedStart, props.zonedEnd); }, [props.isAllDay, props.zonedStart.getTime(), props.zonedEnd.getTime()]);
    var itemTitle = React.useMemo(function () { return "(" + itemTime + "): " + props.title; }, [itemTime, props.title]);
    var calculateMostSiblings = function (slots) {
        var most = 1;
        slots.forEach(function (slot) {
            if (slot.props.items.length > most) {
                most = slot.props.items.length;
            }
        });
        return most;
    };
    var inSlot = function (slot, current) {
        return intersects(slot.start, slot.end, current.start, current.end)
            && slot.group.index === current.group.index
            && slot.range.index === current.range.index
            && (props.ignoreIsAllDay || slot.isAllDay === current.isAllDay);
    };
    var align = function () {
        var slots = (viewSlots.current || []).filter(function (slot) { return inSlot(slot.props, props); });
        if (slots.length === 0) {
            setDisplay(false);
            return;
        }
        var firstSlot = first(slots);
        if (!firstSlot || !item.current) {
            return;
        }
        var rect = getRect(firstSlot.element);
        setRect(item.current.element, rect);
    };
    var position = function () {
        var order = props.order || 0;
        var element = item.current && item.current.element;
        if (!element) {
            return;
        }
        var slots = (viewSlots.current || []).filter(function (slot) { return inSlot(slot.props, props); });
        if (slots.length === 0) {
            setDisplay(false);
            return;
        }
        var mostSiblingsInSlot = calculateMostSiblings(slots);
        if (slots.length === 0) {
            setDisplay(false);
            return;
        }
        var rect = getRect(element);
        rect.width = props.vertical
            ? (props.resizeHint || props.dragHint)
                ? rect.width
                : ((rect.width / mostSiblingsInSlot) - BORDER_WIDTH)
            : ((rect.width * slots.length) - (BORDER_WIDTH));
        rect.height = props.vertical
            ? ((rect.height * slots.length) - (BORDER_WIDTH))
            : (props.resizeHint || props.dragHint)
                ? rect.height
                : 25;
        rect.left = props.vertical
            ? (rect.left + (order * rect.width) + (BORDER_WIDTH * order))
            : rect.left;
        rect.top = props.vertical
            ? rect.top
            : (rect.top + (order * rect.height) + (BORDER_WIDTH * order));
        setRect(element, rect);
        setVisible(true);
    };
    var handleResize = React.useCallback(function () { align(); position(); }, [align, position]);
    React.useEffect(align);
    React.useEffect(position);
    React.useEffect(function () {
        window.addEventListener('resize', handleResize);
        return function () {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);
    return (React.createElement(Item, __assign({}, itemProps, { _ref: item, style: __assign({ visibility: visible ? undefined : 'hidden', display: display ? undefined : 'none' }, props.style) }),
        (!props.resizeHint) && React.createElement("span", { className: "k-event-actions" },
            props.tail && React.createElement("span", { className: "k-icon k-i-arrow-60-left" }),
            (props.isRecurring && !props.isException) && React.createElement("span", { className: "k-icon k-i-reload" }),
            (!props.isRecurring && props.isException) && React.createElement("span", { className: "k-icon k-i-non-recurrence" })),
        (!props.resizeHint) && (React.createElement("div", { title: itemTitle },
            !props.isAllDay && (React.createElement(SchedulerItemContent, { className: "k-event-template k-event-time " }, itemTitle)),
            React.createElement(SchedulerItemContent, { className: "k-event-template" }, props.title)))));
});
var defaultProps = {
    item: SchedulerItem
};
SchedulerViewItem.defaultProps = defaultProps;
SchedulerViewItem.displayName = 'KendoReactSchedulerViewItem';
//# sourceMappingURL=SchedulerViewItem.js.map